// CertificationEngine.js — InvestorQ Private Market
// Shared certification scoring, validation, and workflow logic
// Use this in: OrgApplicationPortal (preview score), OrgAdminPanel (display), orgRoutes (server-side)
// OffBeet Media & Tech Inc. · investorq.io

// ─────────────────────────────────────────────────────────────────────────────
// CERTIFICATION SCORING ENGINE
// Produces a composite score 0–100 with 6 weighted dimensions
// Used for: investor confidence indicator, admin triage, Tier C gate
// ─────────────────────────────────────────────────────────────────────────────

export const SCORE_DIMENSIONS = {
  financials:          { label: 'Financial Quality',     maxPts: 30, weight: 0.30 },
  revenueScale:        { label: 'Revenue & Scale',       maxPts: 20, weight: 0.20 },
  fanEngagement:       { label: 'Fan Engagement',        maxPts: 15, weight: 0.15 },
  mediaBroadcast:      { label: 'Media & Broadcast',     maxPts: 15, weight: 0.15 },
  operationalMaturity: { label: 'Operational Maturity',  maxPts: 10, weight: 0.10 },
  documentReadiness:   { label: 'Document Readiness',    maxPts: 10, weight: 0.10 },
};

export function computeCertScore(f = {}) {
  const breakdown = {};
  let score = 0;

  // ── Financials (30 pts) ────────────────────────────────────────────────────
  let fin = 0;
  if (f.hasAuditedFinancials === 'audited')  fin += 30;
  if (f.hasAuditedFinancials === 'reviewed') fin += 20;
  if (f.hasAuditedFinancials === 'prepared') fin += 10;
  if (f.hasCpa === 'yes')                    fin  = Math.min(30, fin + 5);
  breakdown.financials = Math.min(30, fin);
  score += breakdown.financials;

  // ── Revenue & Scale (20 pts) ───────────────────────────────────────────────
  let rev = 0;
  const band = f.revenueBand || '';
  if (band === '$50M+')        rev = 20;
  else if (band === '$10M–$50M') rev = 16;
  else if (band === '$2M–$10M')  rev = 12;
  else if (band === '$500K–$2M') rev = 7;
  else if (band === '<$500K')    rev = 3;
  if (f.ebitdaPositive === 'yes') rev = Math.min(20, rev + 3);
  if (f.revenueTrend === 'growing_fast') rev = Math.min(20, rev + 2);
  breakdown.revenueScale = Math.min(20, rev);
  score += breakdown.revenueScale;

  // ── Fan Engagement (15 pts) ────────────────────────────────────────────────
  let fan = 0;
  const att = parseInt(f.avgAttendance) || 0;
  if (att > 50000)      fan += 8;
  else if (att > 10000) fan += 6;
  else if (att > 1000)  fan += 3;
  const soc = parseInt(f.socialFollowers) || 0;
  if (soc > 1_000_000)  fan += 7;
  else if (soc > 100000) fan += 5;
  else if (soc > 10000) fan += 2;
  breakdown.fanEngagement = Math.min(15, fan);
  score += breakdown.fanEngagement;

  // ── Media & Broadcast (15 pts) ────────────────────────────────────────────
  let media = 0;
  if (f.tvDeal === 'national')       media = 15;
  else if (f.tvDeal === 'regional')  media = 10;
  else if (f.tvDeal === 'streaming') media = 7;
  else if (f.tvDeal === 'local')     media = 3;
  breakdown.mediaBroadcast = Math.min(15, media);
  score += breakdown.mediaBroadcast;

  // ── Operational Maturity (10 pts) ─────────────────────────────────────────
  let ops = 0;
  const seasons = parseInt(f.seasonsCompleted) || 0;
  if (seasons >= 5)      ops += 5;
  else if (seasons >= 3) ops += 3;
  else if (seasons >= 1) ops += 1;
  if (f.website)         ops += 2;
  if (f.linkedIn)        ops += 1;
  if (f.hasSponsorDeck)  ops += 2;
  breakdown.operationalMaturity = Math.min(10, ops);
  score += breakdown.operationalMaturity;

  // ── Document Readiness (10 pts) ───────────────────────────────────────────
  let docs = 0;
  if (f.hasFinancials || f.docs_financials) docs += 4;
  if (f.hasCapTable  || f.docs_captable)    docs += 3;
  if (f.hasOrgDeck   || f.docs_deck)        docs += 2;
  if (f.hasSponsorDeck)                     docs += 1;
  breakdown.documentReadiness = Math.min(10, docs);
  score += breakdown.documentReadiness;

  const total = Math.min(100, Math.round(score));
  return { total, breakdown };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORE LABEL & COLOR
// ─────────────────────────────────────────────────────────────────────────────

export function scoreMeta(total) {
  if (total >= 80) return { label:'Excellent', color:'#00d4aa', bg:'rgba(0,212,170,0.1)',  border:'rgba(0,212,170,0.25)' };
  if (total >= 70) return { label:'Strong',    color:'#7c6fff', bg:'rgba(124,111,255,0.1)', border:'rgba(124,111,255,0.25)' };
  if (total >= 55) return { label:'Moderate',  color:'#f5a623', bg:'rgba(245,166,35,0.1)', border:'rgba(245,166,35,0.25)' };
  if (total >= 40) return { label:'Developing',color:'#94a3b8', bg:'rgba(148,163,184,0.1)',border:'rgba(148,163,184,0.25)' };
  return                   { label:'Weak',      color:'#f87171', bg:'rgba(248,113,113,0.1)',border:'rgba(248,113,113,0.25)' };
}

// ─────────────────────────────────────────────────────────────────────────────
// TIER C READINESS GATE
// Tier B certified + score ≥ 70 + audited/reviewed financials
// ─────────────────────────────────────────────────────────────────────────────

export function isTierCReady(app) {
  if (!app) return false;
  const { total } = computeCertScore(app);
  return (
    app.status === 'certified' &&
    total >= 70 &&
    ['audited', 'reviewed'].includes(app.has_financials || app.hasAuditedFinancials)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CERTIFICATION CHECKLIST
// Returns a structured checklist with pass/fail for each requirement
// Used in: OrgApplicationPortal (self-assessment), OrgAdminPanel (review)
// ─────────────────────────────────────────────────────────────────────────────

export function buildCertChecklist(formData = {}, targetTier = 'B') {
  const f = formData;

  const TIER_B_ITEMS = [
    {
      id: 'org_info',
      label: 'Organization information complete',
      desc: 'Name, type, sport, country, website, and founding year provided',
      pass: !!(f.orgName && f.sport && f.orgType && f.country),
      required: true,
    },
    {
      id: 'contact_info',
      label: 'Authorized contact verified',
      desc: 'First name, last name, title, and email of authorized representative',
      pass: !!(f.firstName && f.lastName && f.email && f.title),
      required: true,
    },
    {
      id: 'financial_data',
      label: 'Financial data disclosed',
      desc: 'Revenue band, EBITDA status, and financial history type provided',
      pass: !!(f.revenueBand && f.ebitdaPositive && f.hasAuditedFinancials),
      required: true,
    },
    {
      id: 'operations',
      label: 'Operational history disclosed',
      desc: 'Seasons completed, team/franchise count, attendance data',
      pass: !!(f.seasonsCompleted && f.numTeams),
      required: true,
    },
    {
      id: 'docs_financial',
      label: 'Financial statements ready',
      desc: '2+ years GAAP financials (reviewed or audited preferred)',
      pass: !!(f.hasFinancials || f.docs_financials),
      required: true,
    },
    {
      id: 'docs_captable',
      label: 'Cap table / ownership summary',
      desc: 'Ownership structure, equity holders, voting rights summary',
      pass: !!(f.hasCapTable || f.docs_captable),
      required: true,
    },
    {
      id: 'docs_deck',
      label: 'Organization overview deck',
      desc: 'Pitch deck or league overview document',
      pass: !!(f.hasOrgDeck || f.docs_deck),
      required: false,
    },
    {
      id: 'nda_ready',
      label: 'NDA execution commitment',
      desc: 'Org agrees to execute InvestorQ Mutual NDA before document submission',
      pass: true, // assumed on form submission
      required: true,
    },
  ];

  const TIER_C_ITEMS = [
    ...TIER_B_ITEMS,
    {
      id: 'tier_b_cert',
      label: 'Tier B certification complete',
      desc: 'Active Tier B certification required before Tier C raise',
      pass: formData.status === 'certified',
      required: true,
    },
    {
      id: 'audited_fin',
      label: 'Audited financials (for raises > $618K)',
      desc: 'SEC requires audited GAAP financials for Reg CF raises over the threshold',
      pass: f.hasAuditedFinancials === 'audited',
      required: true,
    },
    {
      id: 'bad_actor',
      label: 'Bad actor certification',
      desc: 'Confirmation of no SEC Rule 506(d) disqualification for covered persons',
      pass: !!(f.badActorCleared || f.bad_actor_cleared),
      required: true,
    },
    {
      id: 'broker_partner',
      label: 'Broker-dealer partnership',
      desc: 'Active agreement with DealMaker, Republic, Wefunder, or other SEC-registered BD',
      pass: !!(f.brokerPartner || f.broker_partner),
      required: true,
    },
    {
      id: 'sec_form_c',
      label: 'SEC Form C filed',
      desc: 'Form C submitted to SEC EDGAR via broker-dealer portal',
      pass: !!(f.secFormCFiled || f.sec_form_c_filed),
      required: true,
    },
  ];

  const items = targetTier === 'C' ? TIER_C_ITEMS : TIER_B_ITEMS;
  const required = items.filter(i => i.required);
  const allRequiredPass = required.every(i => i.pass);
  const passCount = items.filter(i => i.pass).length;
  const completionPct = Math.round((passCount / items.length) * 100);

  return { items, allRequiredPass, passCount, total: items.length, completionPct };
}

// ─────────────────────────────────────────────────────────────────────────────
// WORKFLOW PIPELINE DEFINITION
// Status order, transitions, SLA targets, labels
// ─────────────────────────────────────────────────────────────────────────────

export const PIPELINE_STAGES = [
  {
    status: 'pending_review',
    label: 'Pending Review',
    icon: '📋',
    color: '#94a3b8',
    slaHours: 48,
    desc: 'Application submitted — awaiting InvestorQ team initial review',
  },
  {
    status: 'under_review',
    label: 'Under Review',
    icon: '🔍',
    color: '#60a5fa',
    slaHours: 72,
    desc: 'InvestorQ team actively reviewing application materials',
  },
  {
    status: 'nda_sent',
    label: 'NDA Sent',
    icon: '🔒',
    color: '#a78bfa',
    slaHours: 120,
    desc: 'Mutual NDA sent to org — awaiting e-signature',
  },
  {
    status: 'docs_requested',
    label: 'Docs Requested',
    icon: '📂',
    color: '#f5a623',
    slaHours: 240,
    desc: 'NDA executed — org uploading financial documents for verification',
  },
  {
    status: 'certified',
    label: 'Certified — Tier B',
    icon: '◈',
    color: '#7c6fff',
    slaHours: null,
    desc: 'Financials verified, certification granted — deal room live',
  },
  {
    status: 'raise_live',
    label: 'Raise Live — Tier C',
    icon: '◆',
    color: '#f5a623',
    slaHours: null,
    desc: 'Reg CF / Reg A+ raise active via licensed broker-dealer partner',
  },
];

export const PIPELINE_STATUS_MAP = Object.fromEntries(
  PIPELINE_STAGES.map((s, i) => [s.status, { ...s, step: i + 1 }])
);

// ─────────────────────────────────────────────────────────────────────────────
// VALIDATION SCHEMAS (frontend mirrors backend)
// ─────────────────────────────────────────────────────────────────────────────

export const VALID_SPORTS = [
  'Cricket', 'Football', 'Basketball', 'Soccer', 'Baseball',
  'Hockey', 'Tennis', 'Golf', 'MMA', 'Esports', 'Rugby', 'Other',
];

export const VALID_TIERS = ['A', 'B', 'C'];

export const REVENUE_BANDS = ['<$500K', '$500K–$2M', '$2M–$10M', '$10M–$50M', '$50M+'];

export const FINANCIALS_OPTIONS = [
  { value: 'audited',  label: 'CPA-Audited GAAP (strongest — required for raises > $618K)' },
  { value: 'reviewed', label: 'CPA-Reviewed GAAP' },
  { value: 'prepared', label: 'Management-Prepared / Internal' },
  { value: 'none',     label: 'Not yet prepared' },
];

export const TV_DEAL_OPTIONS = [
  { value: 'national',   label: 'National broadcast deal (CBS, NBC, ESPN, etc.)' },
  { value: 'regional',   label: 'Regional broadcast or cable deal' },
  { value: 'streaming',  label: 'Streaming platform deal (YouTube, Amazon, Apple TV)' },
  { value: 'local',      label: 'Local broadcast / community TV' },
  { value: 'none',       label: 'No current broadcast deal' },
];

export function validateApplyPayload(f) {
  const errors = {};

  if (!f.orgName?.trim())     errors.orgName    = 'Organization name is required';
  if (!f.firstName?.trim())   errors.firstName  = 'First name is required';
  if (!f.lastName?.trim())    errors.lastName   = 'Last name is required';
  if (!f.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
    errors.email = 'Valid email address is required';
  if (!VALID_SPORTS.includes(f.sport))
    errors.sport = `Sport must be one of: ${VALID_SPORTS.join(', ')}`;
  if (!VALID_TIERS.includes(f.selectedTier))
    errors.selectedTier = 'Please select a certification tier';
  if (f.website && !/^https?:\/\//.test(f.website))
    errors.website = 'Website must start with https://';

  return { isValid: Object.keys(errors).length === 0, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// CERTIFICATION CONFIDENCE LABEL
// Human-readable explanation of what Tier A/B/C means on a tile
// ─────────────────────────────────────────────────────────────────────────────

export const TIER_CONFIDENCE = {
  A: {
    label:    'Market Intelligence',
    icon:     '◎',
    color:    '#00d4aa',
    investor: 'Editorial valuation estimate using public data. No org consent. Simulation only.',
    org:      'No action required — InvestorQ may list any public organization at Tier A.',
    caveat:   'Valuation is editorial estimate only. Not investment advice.',
  },
  B: {
    label:    'Certified Intelligence',
    icon:     '◈',
    color:    '#7c6fff',
    investor: 'Org-verified financials under NDA. AICPA-aligned 4-method valuation. Deal room access.',
    org:      'Org has executed NDA and submitted 2+ years GAAP financials for InvestorQ review.',
    caveat:   'Certification does not guarantee investment returns. All private market investments are illiquid.',
  },
  C: {
    label:    'Live Investment Raise',
    icon:     '◆',
    color:    '#f5a623',
    investor: 'Active Reg CF or Reg A+ raise. SEC Form C filed. Cleared by licensed broker-dealer.',
    org:      'All Tier B requirements met + active raise via DealMaker, Republic, or Wefunder.',
    caveat:   'Investments cleared by registered broker-dealer. Past performance does not predict future results.',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// INVESTOR INTEREST ELIGIBILITY
// Returns whether an investor can register interest given their inputs
// ─────────────────────────────────────────────────────────────────────────────

export function interestEligibility(investorData = {}, orgStatus = '') {
  const eligible = ['certified', 'raise_live'].includes(orgStatus);
  const ndaRequired = !investorData.ndaExecuted;
  const accreditedRequired = ['raise_live'].includes(orgStatus);
  const warnings = [];

  if (!eligible) warnings.push('Org must be Tier B certified before investor interest can be registered.');
  if (accreditedRequired && !investorData.accredited)
    warnings.push('This raise is for accredited investors under Reg D. Please confirm your accredited status.');

  return { eligible, ndaRequired, accreditedRequired, warnings };
}
